import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { call } from '@packages/functions';
import React, { ReactElement, SyntheticEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

export type MenuVariants = 'icon' | 'button' | 'custom';

export interface BaseMenuProps extends Omit<MenuProps, 'variant' | 'open' | 'onChange'> {
    variant?: MenuVariants;
    icon?: string | ReactElement;
    text?: string;
    toggle?: any;

    toggleProps?: {
        [name: string]: any;
    };

    onActions?: (actions: any) => void;

    onOpen?: (event: Event) => any;

    onChange?: (value: any, e: SyntheticEvent) => void;

    children?: any;
    closeOnSelect?: boolean;
    showMenuAtBottom?: boolean;
}

export const BaseMenu = (props: BaseMenuProps): ReactElement => {
    const {
        children,
        variant,
        icon,
        text,
        onChange,
        onClose,
        onOpen,
        onActions,
        toggleProps,
        toggle,
        PaperProps,
        closeOnSelect,

        ...rest
    } = props;

    const [anchorMenu, setAnchorMenu] = useState(null);

    const handleOpen = (e): any => {
        e.preventDefault();
        e.stopPropagation();

        setAnchorMenu(e.currentTarget);
        call(onOpen, e);

        return false;
    };

    const handleClose = (e): any => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorMenu(null);
        call(onClose, e);
        return false;
    };

    useEffect(() => {
        call(onActions, {
            handleOpen,
            handleClose,
        });
    }, []);

    const children$ = React.Children.map(children, (child) => {
        if (child && (child.type === MenuItem || closeOnSelect)) {
            return {
                ...child,
                props: {
                    ...child.props,
                    onClick(e): void {
                        setAnchorMenu(null);
                        call(child.props.onClick, e);
                        call(onChange, child.props.value ?? e.target.value, e);
                        call(onClose, e);
                    },
                },
            };
        }

        return child;
    });

    const tProps$ = {
        ...toggleProps,
        onClick(e): any {
            e.preventDefault();
            e.stopPropagation();

            handleOpen(e);
            call((toggleProps || {}).onClick, e);

            return false;
        },
    };

    const paperProps$ = {
        elevation: 4,
        ...(PaperProps ?? {}),
    };

    const menu = (
        // @ts-ignore
        <StyledMenu
            anchorEl={anchorMenu}
            variant={'menu'}
            onClose={handleClose}
            open={!!anchorMenu}
            PaperProps={paperProps$}
            {...rest}
        >
            {children$}
        </StyledMenu>
    );

    return (
        <>
            {VARIANTS[variant || 'button']({ icon, text, toggle, toggleProps: tProps$ })}
            {menu}
        </>
    );
};

const VARIANTS = {
    icon: ({ icon, toggleProps }): ReactElement => (
        <IconButton {...(toggleProps || {})}>
            <Icon>{icon || 'more_vert'}</Icon>
        </IconButton>
    ),

    button: ({ text, toggleProps }): ReactElement => <Button {...(toggleProps || {})}>{text || 'Open Menu'}</Button>,

    custom: ({ toggle, toggleProps }): ReactElement => ({
        ...toggle,
        props: {
            ...(toggle.props || {}),
            ...toggleProps,
        },
    }),
};

export const StyledMenu = styled(Menu)<any>`
    && {
        ${(props) =>
            props.showMenuAtBottom
                ? `
                .MuiPaper-root {
                    &.MuiMenu-paper {
                        min-height: 68px;
                        display: flex;
                        align-items: center;
                        border-radius: 12px;
                        background-color: #ffffff;
                        top: unset !important;
                        bottom: 0;
                        left: unset !important;
                        width: -webkit-fill-available;
                        margin: 0 16px 16px;
                        box-shadow: 0px 12px 17px 0px rgba(0, 0, 0, 0.14), 0px 5px 22px 0px rgba(0, 0, 0, 0.12),
                            0px 7px 8px 0px rgba(0, 0, 0, 0.2);
                    }
                    &::before {
                        content: '';
                        width: 40px;
                        height: 4px;
                        background-color: rgba(0, 0, 0, 0.24);
                        position: absolute;
                        top: 8px;
                        left: 50%;
                        right: 50%;
                        border-radius: 4px;
                    }
                }

                .MuiMenu-list {
                    width: 100%;
                }
            `
                : `
                .MuiPaper-root.MuiMenu-paper {
                    background-color: rgba(255, 255, 255, 0.8);
                    backdrop-filter: saturate(180%) blur(20px);
                    box-shadow: 0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12),
                        0px 8px 10px rgba(0, 0, 0, 0.2);
                }
                
                .MuiListItemIcon-root {
                    min-width: 36px;
                }
            `}
    }
`;
